import { useAuth } from "hooks/useAuth";
import React, { useState, useEffect, useCallback, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Disclosure, Menu } from "@headlessui/react";
import {
  Bars3Icon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  BugAntIcon,
  LightBulbIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import logo from "assets/images/icon.png";
import { ImpersonationControl } from "./ImpersonationControl";
import RoleSwitcher from "./RoleSwitcher";
import { fetchUnreadActivityCount } from "api/partnerActivity";
import { useRole } from "hooks/useRole";
import { lazyWithRetry } from "utils/lazyWithRetry";
import { classNames } from "./LayoutUtils";

// Lazy load modals to reduce initial bundle size
const BugReportModal = lazyWithRetry(() => import("./BugReportModal"));
const IdeaSubmissionModal = lazyWithRetry(
  () => import("./IdeaSubmissionModal")
);
const RoleSelectionModal = lazyWithRetry(() => import("./RoleSelectionModal"));

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  const [isIdeaModalOpen, setIsIdeaModalOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const impersonationControl = ImpersonationControl();
  const { role } = useRole();

  // Memoized on `role` so the polling effect below can depend on it directly.
  // Without useCallback this is a new function every render, and listing it as
  // a dep would tear down and rebuild the 30s interval on each render, so it
  // could never fire. Must stay above the effect that lists it as a dep.
  const loadUnreadCount = useCallback(async () => {
    try {
      // Exclude notifications from my own messages
      const count = await fetchUnreadActivityCount(role || undefined);
      setUnreadCount(count);
    } catch (error) {
      console.error("Failed to load unread count:", error);
    }
  }, [role]);

  useEffect(() => {
    if (user?.ID) {
      loadUnreadCount();
      // Poll every 30 seconds for new activities
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.ID, loadUnreadCount]);
  const displayName =
    user?.name ||
    user?.display_name ||
    user?.user_nicename ||
    user?.user_login ||
    "User";
  const emailAddress = user?.email || user?.user_email || "";

  const navigation = [
    { name: "Home", href: "/", current: true },
    {
      name: "Blog",
      href: "https://www.uncoveringintimacy.com",
      current: false,
    },
    {
      name: "Store",
      href: "https://www.uncoveringintimacy.com/shop",
      current: false,
    },
    ...(user?.ID === 555
      ? [{ name: "Admin", href: "/Admin", current: false }]
      : []),
  ];

  const userNavigation = [
    { name: "Invite Spouse", href: "/OurSexplorationList/InviteSpouse" },
    { name: "Settings", href: "/Settings" },
    ...(user?.ID === 555 ? [{ name: "Admin Dashboard", href: "/Admin" }] : []),
    { name: "Log out", href: "/Logout" },
  ];

  return (
    // pb-safe-bottom keeps the end of every page clear of the Android gesture
    // bar. On the nav, the safe-area padding sits inside the element's own
    // background box, so bg-gray-800 still fills the status bar area edge to
    // edge while the logo and links are pushed below it (and clear of a
    // landscape notch). All four are 0 on the web.
    <div className="min-h-full pb-safe-bottom">
      <Disclosure
        as="nav"
        className="bg-gray-800 pl-safe-left pr-safe-right pt-safe-top"
      >
        {({ open }) => (
          <>
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Link to="/">
                      <img
                        className="h-8 w-8 cursor-pointer"
                        src={logo}
                        alt="Uncovering Intimacy"
                      />
                    </Link>
                  </div>
                  <div className="hidden md:block">
                    <div className="ml-10 flex items-baseline space-x-4">
                      {navigation.map((item) =>
                        item.href.startsWith("http") ? (
                          <a
                            key={item.name}
                            href={item.href}
                            className={classNames(
                              item.current
                                ? "bg-gray-900 text-white"
                                : "text-gray-300 hover:bg-gray-700 hover:text-white",
                              "rounded-md px-3 py-2 text-sm font-medium"
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {item.name}
                          </a>
                        ) : (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={classNames(
                              item.current
                                ? "bg-gray-900 text-white"
                                : "text-gray-300 hover:bg-gray-700 hover:text-white",
                              "rounded-md px-3 py-2 text-sm font-medium"
                            )}
                            aria-current={item.current ? "page" : undefined}
                          >
                            {item.name}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                </div>
                <div className="hidden md:block">
                  <div className="ml-4 flex items-center md:ml-6 gap-3">
                    {/* Which spouse is at the keyboard. One account, two
                        people, so this is how the other one takes a turn.
                        Hidden until a role is chosen, because the selection
                        modal owns that first decision. */}
                    {role && <RoleSwitcher />}
                    {/* What's New Icon - Always visible */}
                    <button
                      type="button"
                      onClick={() => navigate("/Settings/WhatsNew")}
                      className="rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                      title="What's New"
                    >
                      <span className="sr-only">What's new</span>
                      <SparklesIcon
                        className="h-6 w-6 text-purple-400 hover:text-purple-300"
                        aria-hidden="true"
                      />
                    </button>

                    {user ? (
                      <React.Fragment>
                        {/* Impersonation Control for user 555 */}
                        {impersonationControl && (
                          <div>
                            {impersonationControl.isCurrentlyImpersonating ? (
                              <button
                                onClick={
                                  impersonationControl.handleStopImpersonating
                                }
                                className="rounded-full bg-yellow-500 text-black p-1 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                                title="Stop Impersonating"
                              >
                                <span className="sr-only">
                                  Stop Impersonating (ID: {user?.ID})
                                </span>
                                <svg
                                  className="h-6 w-6"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="2"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                                  />
                                </svg>
                              </button>
                            ) : (
                              <>
                                {impersonationControl.showInput ? (
                                  <div className="flex gap-2">
                                    <input
                                      type="number"
                                      value={impersonationControl.targetUserId}
                                      onChange={(e) =>
                                        impersonationControl.setTargetUserId(
                                          e.target.value
                                        )
                                      }
                                      placeholder="User ID"
                                      className="w-24 rounded-md border-gray-300 px-2 py-1 text-sm text-gray-900"
                                      disabled={impersonationControl.loading}
                                    />
                                    <button
                                      onClick={
                                        impersonationControl.handleImpersonate
                                      }
                                      disabled={
                                        !impersonationControl.targetUserId ||
                                        impersonationControl.loading
                                      }
                                      className="rounded-md bg-indigo-600 text-white px-2 py-1 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                      {impersonationControl.loading
                                        ? "..."
                                        : "Go"}
                                    </button>
                                    <button
                                      onClick={() =>
                                        impersonationControl.setShowInput(false)
                                      }
                                      className="rounded-md bg-gray-600 text-white px-2 py-1 text-sm font-medium hover:bg-gray-700"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() =>
                                      impersonationControl.setShowInput(true)
                                    }
                                    className="rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                                    title="Impersonate User"
                                  >
                                    <span className="sr-only">
                                      Impersonate User
                                    </span>
                                    <svg
                                      className="h-6 w-6"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth="2"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                                      />
                                    </svg>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {/* Idea Submission Icon */}
                        <button
                          type="button"
                          onClick={() => setIsIdeaModalOpen(true)}
                          className="rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                          title="Submit an Idea"
                        >
                          <span className="sr-only">Submit an idea</span>
                          <LightBulbIcon
                            className="h-6 w-6 text-yellow-400 hover:text-yellow-300"
                            aria-hidden="true"
                          />
                        </button>

                        {/* Bug Report Icon */}
                        <button
                          type="button"
                          onClick={() => setIsBugReportOpen(true)}
                          className="rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                          title="Report a Bug"
                        >
                          <span className="sr-only">Report a bug</span>
                          <BugAntIcon
                            className="h-6 w-6 text-[#dd3333] hover:text-[#ff4444]"
                            aria-hidden="true"
                          />
                        </button>

                        {/* Chat Icon */}
                        <button
                          type="button"
                          onClick={() => navigate("/Activities")}
                          className="relative rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                        >
                          <span className="sr-only">View chat</span>
                          <ChatBubbleLeftRightIcon
                            className="h-6 w-6"
                            aria-hidden="true"
                          />
                          {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-semibold text-white">
                              {unreadCount > 9 ? "9+" : unreadCount}
                            </span>
                          )}
                        </button>

                        {/* Profile dropdown */}
                        <Menu as="div" className="relative ml-3">
                          <div>
                            <Menu.Button className="flex max-w-xs items-center rounded-full bg-gray-800 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                              <span className="sr-only">Open user menu</span>
                              <div className="px-3 py-1 text-sm font-medium">
                                {displayName}
                              </div>
                              {/* <img
                                className="h-8 w-8 rounded-full"
                                src={user.imageUrl}
                                alt=""
                              /> */}
                            </Menu.Button>
                          </div>
                          <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0">
                            {userNavigation.map((item) => (
                              <Menu.Item key={item.name}>
                                {({ active }) => (
                                  <a
                                    href={item.href}
                                    className={classNames(
                                      active ? "bg-gray-100" : "",
                                      "block px-4 py-2 text-sm text-gray-700"
                                    )}
                                  >
                                    {item.name}
                                  </a>
                                )}
                              </Menu.Item>
                            ))}
                          </Menu.Items>
                        </Menu>
                      </React.Fragment>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                <div className="-mr-2 flex items-center gap-3 md:hidden">
                  {role && <RoleSwitcher />}
                  {/* What's New Icon - Always visible */}
                  <button
                    type="button"
                    onClick={() => navigate("/Settings/WhatsNew")}
                    className="rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                    title="What's New"
                  >
                    <span className="sr-only">What's new</span>
                    <SparklesIcon
                      className="h-6 w-6 text-purple-400 hover:text-purple-300"
                      aria-hidden="true"
                    />
                  </button>

                  {/* Mobile notifications/chat button */}
                  {user && (
                    <>
                      {/* Idea Submission Icon */}
                      <button
                        type="button"
                        onClick={() => setIsIdeaModalOpen(true)}
                        className="rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                        title="Submit an Idea"
                      >
                        <span className="sr-only">Submit an idea</span>
                        <LightBulbIcon
                          className="h-6 w-6 text-yellow-400 hover:text-yellow-300"
                          aria-hidden="true"
                        />
                      </button>

                      <button
                        type="button"
                        onClick={() => setIsBugReportOpen(true)}
                        className="rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                        title="Report a Bug"
                      >
                        <span className="sr-only">Report a bug</span>
                        <BugAntIcon
                          className="h-6 w-6 text-[#dd3333] hover:text-[#ff4444]"
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate("/Activities")}
                        className="relative rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                      >
                        <span className="sr-only">View chat</span>
                        <ChatBubbleLeftRightIcon
                          className="h-6 w-6"
                          aria-hidden="true"
                        />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-semibold text-white">
                            {unreadCount > 9 ? "9+" : unreadCount}
                          </span>
                        )}
                      </button>
                    </>
                  )}
                  {/* Mobile menu button */}
                  <Disclosure.Button className="inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="md:hidden">
              {/* Navigation Links */}
              <div className="space-y-1 px-2 pb-3 pt-2">
                {navigation.map((item) =>
                  item.href.startsWith("http") ? (
                    <Disclosure.Button
                      key={item.name}
                      as="a"
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classNames(
                        item.current
                          ? "bg-gray-900 text-white"
                          : "text-gray-300 hover:bg-gray-700 hover:text-white",
                        "block rounded-md px-3 py-2 text-base font-medium"
                      )}
                    >
                      {item.name}
                    </Disclosure.Button>
                  ) : (
                    <Disclosure.Button
                      key={item.name}
                      as={Link}
                      to={item.href}
                      className={classNames(
                        item.current
                          ? "bg-gray-900 text-white"
                          : "text-gray-300 hover:bg-gray-700 hover:text-white",
                        "block rounded-md px-3 py-2 text-base font-medium"
                      )}
                      aria-current={item.current ? "page" : undefined}
                    >
                      {item.name}
                    </Disclosure.Button>
                  )
                )}
              </div>

              {/* User Profile Section */}
              {user ? (
                <>
                  <div className="border-t border-gray-700 pt-4 pb-3">
                    {/* User Info */}
                    <div className="flex items-center px-5 pb-3">
                      <div className="flex-1">
                        <div className="text-base font-medium text-white">
                          {displayName}
                        </div>
                        <div className="text-sm font-medium text-gray-400">
                          {emailAddress}
                        </div>
                      </div>
                    </div>

                    {/* Impersonation Control for user 555 */}
                    {impersonationControl && (
                      <div className="px-5 pb-3">
                        {impersonationControl.isCurrentlyImpersonating ? (
                          <button
                            onClick={
                              impersonationControl.handleStopImpersonating
                            }
                            className="w-full flex items-center justify-center gap-2 rounded-md bg-yellow-500 text-black px-3 py-2 text-base font-medium hover:bg-yellow-400"
                          >
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="2"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                              />
                            </svg>
                            Stop Impersonating (ID: {user?.ID})
                          </button>
                        ) : (
                          <>
                            {impersonationControl.showInput ? (
                              <div className="space-y-2">
                                <input
                                  type="number"
                                  value={impersonationControl.targetUserId}
                                  onChange={(e) =>
                                    impersonationControl.setTargetUserId(
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter User ID"
                                  className="w-full rounded-md border-gray-300 px-3 py-2 text-base text-gray-900"
                                  disabled={impersonationControl.loading}
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={
                                      impersonationControl.handleImpersonate
                                    }
                                    disabled={
                                      !impersonationControl.targetUserId ||
                                      impersonationControl.loading
                                    }
                                    className="flex-1 rounded-md bg-indigo-600 text-white px-3 py-2 text-base font-medium hover:bg-indigo-700 disabled:opacity-50"
                                  >
                                    {impersonationControl.loading
                                      ? "..."
                                      : "Impersonate"}
                                  </button>
                                  <button
                                    onClick={() =>
                                      impersonationControl.setShowInput(false)
                                    }
                                    className="rounded-md bg-gray-600 text-white px-3 py-2 text-base font-medium hover:bg-gray-700"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() =>
                                  impersonationControl.setShowInput(true)
                                }
                                className="w-full flex items-center justify-center gap-2 rounded-md bg-gray-700 text-white px-3 py-2 text-base font-medium hover:bg-gray-600"
                              >
                                <svg
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="2"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                                  />
                                </svg>
                                Impersonate User
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Account Settings */}

                    {/* Account Settings */}
                    <div className="space-y-1 px-2 border-t border-gray-700 pt-3">
                      {userNavigation.map((item) => (
                        <Disclosure.Button
                          key={item.name}
                          as="a"
                          href={item.href}
                          className="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white"
                        >
                          {item.name}
                        </Disclosure.Button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="border-t border-gray-700 pt-4 pb-3">
                  <div className="px-5">
                    <div className="text-base font-medium text-white">
                      Sign in
                    </div>
                    <div className="text-sm font-medium text-gray-400">
                      to manage your account
                    </div>
                  </div>
                </div>
              )}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      {/*
        Each modal is mounted only when it could actually display, so its chunk
        is fetched on demand rather than on every page load. Mounting them
        unconditionally rendered nothing (each one self-gates internally) but
        still pulled three chunks down on /Login for logged-out visitors and
        crawlers, which is how bingbot produced GlitchTip UI-APP-6 and
        UI-APP-7. The components keep their own guards; this is about the
        fetch, not about what they draw.

        `user && !role` is the same condition RoleSelectionModal uses. useRole
        reads the stored role synchronously in its useState initializer, so
        there is no window where role is null merely because it has not loaded
        yet, and no flash of this modal for a member who already has one.
      */}
      {user && !role && (
        <Suspense fallback={null}>
          <RoleSelectionModal />
        </Suspense>
      )}
      {children}
      {isBugReportOpen && (
        <Suspense fallback={null}>
          <BugReportModal
            isOpen={isBugReportOpen}
            onClose={() => setIsBugReportOpen(false)}
          />
        </Suspense>
      )}
      {isIdeaModalOpen && (
        <Suspense fallback={null}>
          <IdeaSubmissionModal
            isOpen={isIdeaModalOpen}
            onClose={() => setIsIdeaModalOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );

  // return (
  //   <Navbar bg="light" expand="lg" className="mb-4">
  //     <Container>
  //       <Row>
  //         <Col xs={3}>
  //           <Navbar.Toggle aria-controls="basic-navbar-nav" />
  //         </Col>
  //         <Col xs={9}>
  //           <Navbar.Brand href="/">
  //             <img alt="Uncovering Intimacy Logo" src={logo} className="logo" />
  //           </Navbar.Brand>
  //         </Col>
  //       </Row>

  //       <Navbar.Collapse id="basic-navbar-nav">
  //         <Nav className="me-auto">
  //           <Nav.Link href="/">Home</Nav.Link>
  //           <Nav.Link href="https://www.uncoveringintimacy.com/">Blog</Nav.Link>

  //           <NavDropdown title="Account" id="basic-nav-dropdown">
  //             {user.isLoggedIn ? (
  //               <React.Fragment>
  //                 <NavDropdown.Item href="/Settings">Settings</NavDropdown.Item>
  //                 <NavDropdown.Item onClick={handleLogout}>
  //                   Logout
  //                 </NavDropdown.Item>
  //               </React.Fragment>
  //             ) : (
  //               <React.Fragment>
  //                 <NavDropdown.Item
  //                   href="https://www.uncoveringintimacy.com/register"
  //                   target="_blank"
  //                 >
  //                   Register
  //                 </NavDropdown.Item>
  //                 <NavDropdown.Item
  //                   href="https://www.uncoveringintimacy.com/lostpassword"
  //                   target="_blank"
  //                 >
  //                   Lost password
  //                 </NavDropdown.Item>
  //               </React.Fragment>
  //             )}

  //             {/* <NavDropdown.Item href="#action/3.2">
  //               Another action
  //             </NavDropdown.Item>
  //             <NavDropdown.Item href="#action/3.3">Something</NavDropdown.Item>
  //             <NavDropdown.Divider />
  //             <NavDropdown.Item href="#action/3.4">
  //               Separated link
  //             </NavDropdown.Item> */}
  //           </NavDropdown>
  //         </Nav>
  //       </Navbar.Collapse>
  //     </Container>
  //   </Navbar>
  // );
}
